import OpenQuestionSlider from "./OpenQuestionSlider"
import MultipleData from "./MultipleData"
import ScaleData from "./ScaleData"
import ParagraphData from "./ParagraphData"
import MatrixData from "./MatrixData"
import NumberData from "./NumberData"
import { supportsTotalGrowth } from "../../helpers/visualisationDefaults"

/**
 * @param {object} props
 * @param {string} props.effectId
 * @param {string} props.type
 * @param {string} props.fieldId
 * @param {{ ID: string }} [props.indicator]
 * @param {string} [props.startDate]
 * @param {string} [props.endDate]
 * @param {string} props.researchId
 * @param {string} [props.graphType]
 * @param {string} [props.personaId]
 * @param {'graph' | 'text' | 'both'} [props.paragrapgMomentVisibilityStyle]
 * @param {string} [props.projectId]
 * @param {Array<string>} [props.colors]
 * @param {Array<string>} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {'positive-first'|'negative-first'|''} [props.scaleColors] - Kleur de antwoorden van een keuzevraag als schaal
 * @param {string} [props.numberFontSize] - CSS font-size for the total-growth number view.
 * @param {'left'|'center'|'right'} [props.numberTextAlign] - Alignment for the total-growth number view.
 * @param {string} [props.numberFontWeight] - Font weight for the total-growth number view.
 * @param {boolean} [props.filterByResearchId]
 * @param {(total: number) => void} [props.onTotalResponsesChange]
 * @param {(averagesByProject: Object<string, string>) => void} [props.onProjectBreakdownChange] - Per-project averages, bubbled up from Scale/Number children.
 * @returns
 */
const DataType = ({
    effectId,
    type,
    fieldId,
    indicator,
    startDate,
    endDate,
    researchId,
    graphType,
    graphColor,
    personaId,
    paragrapgMomentVisibilityStyle,
    projectId,
    colors,
    colorPalette,
    colorOverrides,
    scaleColors,
    primaryColorOverride,
    numberFontSize,
    numberTextAlign,
    numberFontWeight,
    filterByResearchId,
    onTotalResponsesChange,
    onProjectBreakdownChange,
    onCompareData,
   }) => {

    // Determine the correct field value
    const field = indicator?.ID || fieldId;

    // 'total-growth' is a matrix-only view; for any other field type fall back
    // to its normal chart so the toggle never renders blank.
    const safeGraphType = (graphType === 'total-growth' && !supportsTotalGrowth(type))
        ? ''
        : graphType;

     // Function to render chart type
     const renderData = () => {
        switch (type) {
          case 'multiple-one':
          case 'multiple-multiple':
            return (
            <MultipleData
            researchId={researchId}
            field={field}
            startDate={startDate}
            endDate={endDate}
            graphType={safeGraphType}
            graphColor={graphColor}
            personaId={personaId}
            filterByResearchId={filterByResearchId}
            colorPalette={colorPalette}
            colorOverrides={colorOverrides}
            scaleColors={scaleColors}
            onTotalResponsesChange={onTotalResponsesChange}
            onCompareData={onCompareData}
            />
            );

          case 'scale':
            return (
            <ScaleData
            effectId={effectId}
            researchId={researchId}
            field={field}
            startDate={startDate}
            endDate={endDate}
            graphType={safeGraphType}
            graphColor={graphColor}
            projectId={projectId}
            colors={colors}
            colorPalette={colorPalette}
            personaId={personaId}
            type={type}
            filterByResearchId={filterByResearchId}
            primaryColorOverride={primaryColorOverride}
            onTotalResponsesChange={onTotalResponsesChange}
            onProjectBreakdownChange={onProjectBreakdownChange}
            />
            );

          case 'number':
            return (
            <NumberData
            effectId={effectId}
            researchId={researchId}
            field={field}
            startDate={startDate}
            endDate={endDate}
            graphType={safeGraphType}
            graphColor={graphColor}
            projectId={projectId}
            colors={colors}
            personaId={personaId}
            filterByResearchId={filterByResearchId}
            primaryColorOverride={primaryColorOverride}
            onTotalResponsesChange={onTotalResponsesChange}
            onProjectBreakdownChange={onProjectBreakdownChange}
            onCompareData={onCompareData}
            />
            );

          case 'paragraph':
            { if (paragrapgMomentVisibilityStyle === 'both') {
              return (
                <> 
                  <ParagraphData
                  effectId={effectId}
                  researchId={researchId}
                  field={field}
                  startDate={startDate}
                  endDate={endDate}
                  graphType={safeGraphType}
                  graphColor={graphColor}
                  projectId={projectId}
                  colors={colors}
                  colorPalette={colorPalette}
                  colorOverrides={colorOverrides}
                  personaId={personaId}
                  filterByResearchId={filterByResearchId}
                  onTotalResponsesChange={onTotalResponsesChange}
                  />
                  <OpenQuestionSlider
                  effectId={effectId}
                  researchId={researchId}
                  field={field}
                  startDate={startDate}
                  endDate={endDate}
                  projectId={projectId}
                  colors={colors}
                  personaId={personaId}
                  filterByResearchId={filterByResearchId}
                  onTotalResponsesChange={onTotalResponsesChange}
                  />
                </>
              );
              } else if(paragrapgMomentVisibilityStyle === 'graph'){
                return ( 
                  <ParagraphData
                  effectId={effectId}
                  researchId={researchId}
                  field={field}
                  startDate={startDate}
                  endDate={endDate}
                  graphType={safeGraphType}
                  graphColor={graphColor}
                  projectId={projectId}
                  colors={colors}
                  colorPalette={colorPalette}
                  colorOverrides={colorOverrides}
                  personaId={personaId}
                  filterByResearchId={filterByResearchId}
                  onTotalResponsesChange={onTotalResponsesChange}
                  />
                );
              } else if(paragrapgMomentVisibilityStyle === 'text' || paragrapgMomentVisibilityStyle === undefined){
                return (
                  <OpenQuestionSlider
                  effectId={effectId}
                  researchId={researchId}
                  field={field}
                  startDate={startDate}
                  endDate={endDate}
                  projectId={projectId}
                  colors={colors}
                  personaId={personaId}
                  filterByResearchId={filterByResearchId}
                  onTotalResponsesChange={onTotalResponsesChange}
                  />
                )
              } 
            }
            
        case 'matrix-one':
        case 'matrix-multiple':
            return (
            <MatrixData
            effectId={effectId}
            researchId={researchId}
            field={field}
            startDate={startDate}
            endDate={endDate}
            personaId={personaId}
            graphType={safeGraphType}
            graphColor={graphColor}
            projectId={projectId}
            colors={colors}
            colorPalette={colorPalette}
            colorOverrides={colorOverrides}
            numberFontSize={numberFontSize}
            numberTextAlign={numberTextAlign}
            numberFontWeight={numberFontWeight}
            filterByResearchId={filterByResearchId}
            onTotalResponsesChange={onTotalResponsesChange}
            />
            );

          default:
            return <p>Unknown type</p>;
        }
      };


    return <>
    {renderData()}
    </>;

}

export default DataType